/**
 * Phase 6 · Reporting suite: every server report with a date window,
 * rendered as a responsive table, plus one-click CSV export (server-rendered)
 * and multi-branch comparison.
 */
import { useEffect, useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import { http, ApiError } from '../api/client';
import { useUi } from '../store/ui';
import { money, num, pct, dateTime } from '../lib/format';
import { DataTable, type Column } from '../components/ui/DataTable';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';

interface ReportDef { key: string; label: string; path: string; perm: string }

const REPORTS: ReportDef[] = [
  { key: 'sales_daily', label: 'Sales by day', path: '/reports/sales/daily', perm: 'reports:sales' },
  { key: 'sales_hourly', label: 'Sales by hour', path: '/reports/sales/hourly', perm: 'reports:sales' },
  { key: 'sales_items', label: 'Item sales', path: '/reports/sales/items', perm: 'reports:sales' },
  { key: 'sales_categories', label: 'Category sales', path: '/reports/sales/categories', perm: 'reports:sales' },
  { key: 'sales_payments', label: 'Payment methods', path: '/reports/sales/payment-methods', perm: 'reports:sales' },
  { key: 'voids', label: 'Voids & refunds', path: '/reports/sales/voids', perm: 'reports:sales' },
  { key: 'purchase_summary', label: 'Purchase summary', path: '/reports/purchase/summary', perm: 'reports:purchase' },
  { key: 'purchase_items', label: 'Purchase by item', path: '/reports/purchase/items', perm: 'reports:purchase' },
  { key: 'stock_value', label: 'Inventory valuation', path: '/reports/inventory/valuation', perm: 'reports:inventory' },
  { key: 'stock_moves', label: 'Stock movements', path: '/reports/inventory/movements', perm: 'reports:inventory' },
  { key: 'expiry', label: 'Expiry report', path: '/reports/inventory/expiry', perm: 'reports:inventory' },
  { key: 'wastage', label: 'Wastage', path: '/reports/inventory/wastage', perm: 'reports:inventory' },
  { key: 'recipe_cost', label: 'Recipe costing', path: '/reports/recipe-cost', perm: 'reports:cost' },
  { key: 'pnl', label: 'Profit & loss', path: '/reports/profit-loss', perm: 'reports:financial' },
  { key: 'top_customers', label: 'Top customers', path: '/reports/customers/top', perm: 'reports:customers' },
  { key: 'feedback', label: 'Customer feedback', path: '/reports/customers/feedback', perm: 'reports:customers' },
  { key: 'compare', label: 'Branch comparison', path: '/reports/branches/compare', perm: 'reports:compare' },
];

type Cell = string | number | boolean | null;
type Row = { id: string } & Record<string, Cell>;

/** Render any scalar the way a finance user expects. */
function renderCell(key: string, v: Cell) {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  const k = key.toLowerCase();
  if (/(value|total|sales|cost|price|amount|paid|outstanding|variance|margin$|revenue|expense)/.test(k) && typeof v === 'number' && !k.includes('pct') && !k.includes('count') && !k.includes('qty') && !k.includes('orders') && !k.includes('guests')) return money(v);
  if (k.includes('pct') || k.includes('rate')) return typeof v === 'number' ? pct(v) : String(v);
  if (/_at$|date/.test(k) && typeof v === 'string' && v.length >= 10) return v.length > 10 ? dateTime(v) : v;
  if (typeof v === 'number') return num(v, 2);
  return String(v);
}

export function ReportsPage() {
  const toast = useUi((s) => s.toast);
  const [def, setDef] = useState<ReportDef>(REPORTS[0]);
  const [from, setFrom] = useState(new Date(Date.now() - 6 * 864e5).toISOString().slice(0, 10));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState<Row[]>([]);
  const [extra, setExtra] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let dead = false;
    setLoading(true);
    http.get<Record<string, unknown>>(`${def.path}`, { from, to })
      .then((data) => {
        if (dead) return;
        const arr = Object.values(data).find((v) => Array.isArray(v)) as Row[] | undefined;
        setRows((arr ?? []).map((r, i) => ({ ...r, id: String(r.id ?? i) })));
        const rest: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(data)) if (!Array.isArray(v)) rest[k] = v;
        setExtra(Object.keys(rest).length ? rest : null);
      })
      .catch((e) => { if (!dead) toast({ kind: 'error', title: 'Report failed', message: e instanceof ApiError ? e.message : String(e) }); })
      .finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
  }, [def, from, to, toast]);

  const columns = useMemo<Column<Row>[]>(() => {
    const keys: string[] = [];
    for (const r of rows.slice(0, 20)) for (const k of Object.keys(r)) if (k !== 'id' && !keys.includes(k)) keys.push(k);
    return keys.slice(0, 9).map((k) => ({
      key: k,
      header: k.replace(/_/g, ' '),
      render: (r: Row) => renderCell(k, r[k] as Cell),
    }));
  }, [rows]);

  const downloadCsv = async () => {
    try {
      const text = await http.get<string>(`${def.path}`, { from, to, format: 'csv' });
      const blob = new Blob([typeof text === 'string' ? text : JSON.stringify(text)], { type: 'text/csv' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${def.key}_${from}_${to}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast({ kind: 'error', title: 'Export failed', message: e instanceof ApiError ? e.message : String(e) });
    }
  };

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">Reports</h1>
          <p className="text-sm text-ink-muted">{def.label}</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <Input label="From" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          <Input label="To" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          <Button variant="secondary" onClick={() => void downloadCsv()}><Download size={15} /> CSV</Button>
        </div>
      </header>

      <div className="flex flex-wrap gap-1 rounded-lg bg-surface-1 p-1">
        {REPORTS.map((r) => (
          <button key={r.key} onClick={() => setDef(r)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${def.key === r.key ? 'bg-surface-0 text-ink shadow-sm' : 'text-ink-muted hover:text-ink'}`}>
            {r.label}
          </button>
        ))}
      </div>

      {extra && (
        <pre className="max-h-40 overflow-auto rounded-lg bg-surface-1 p-3 font-mono text-xs text-ink">{JSON.stringify(extra, null, 2)}</pre>
      )}

      <DataTable columns={columns} rows={rows} loading={loading} />
    </div>
  );
}
